import * as endpoints from "../../lib/api/endpoints";
import * as queryStringHelpers from "../../lib/queryStringHelpers";
import * as routes from "../../constants/routes";
import * as validationLib from "../../lib/validation";
import ActionButton from "../ActionButton";
import callApplicationLead from "../../lib/callApplicationLead";
import cardsImage from "../../images/stacked-cards.png";
import cardsImage2 from "../../images/stacked-cards@2x.png";
import cardsImage3 from "../../images/stacked-cards@3x.png";
import Column from "../grid/Column";
import Container from "../grid/Container";
import DateOfBirthInput from "../DateOfBirthInput";
import ErrorBoundary from "../ErrorBoundary";
import ExpandableName from "../ExpandableName";
import ExpiredInviteModal from "../ExpiredInviteModal";
import getFirstInvalidKey, {ValidationType} from "../../lib/getFirstInvalidKey";
import getUserIds from "../../lib/getUserIds";
import hasFailingValidation from "../../lib/hasFailingValidation";
import isAddressKey from "../../lib/isAddressKey";
import isApplicationKey from "../../lib/isApplicationKey";
import LandingPageWrapper from "../LandingPageWrapper";
import LinedInput, {InputType} from "../LinedInput";
import React, {useState, useRef, useEffect} from "react";
import Row from "../grid/Row";
import SetNoIndex from "../../lib/SetNoIndex";
import styles from "./ForgotInvite.module.css";
import useFocusInvalid from "../../lib/useFocusInvalid";
import usePageView from "../../lib/usePageView";
import validateApplicationField from "../../lib/validateApplicationField";
import ValidationList from "../ValidationList";
import {
    AppKeys,
    AddressKeys,
    ForgotInfo,
    ForgotInfoValidation,
    InviteValidationStatus,
} from "../../lib/api/models";
import {RouteComponentProps} from "react-router-dom";

const strings = {
    card: "Credit card",
    continue: "Continue",
    dateOfBirth: "Date of Birth (MM/DD/YYYY)",
    description:
        "To retrieve your account info, please fill out the following fields and we will redirect you to your application",
    expiredModal: "Expired Invite Code Modal",
    title: "1st Financial Bank USA Invitation Help",
    zipcode: "ZIP Code",
};

const EMPTY_FORGOT_INFO_VALIDATION: ForgotInfoValidation = {};

const IS_DEV = process.env.NODE_ENV === "development";

const EMPTY_FORGOT_INFO: ForgotInfo = {
    [AppKeys.DateOfBirth]: IS_DEV ? "01/01/2000" : "",
    [AppKeys.FirstName]: IS_DEV ? "John" : "",
    [AppKeys.MiddleInitial]: IS_DEV ? "A" : "",
    [AppKeys.LastName]: IS_DEV ? "Smith" : "",
    [AddressKeys.Zipcode]: IS_DEV ? "12345" : "",
};

type Props = RouteComponentProps & {
    findInviteDescription?: string;
    renderInterestRates?: boolean;
};

export default function ForgotInvite(props: Props) {
    useEffect(() => {
        document.title = strings.title;
    }, []);
    SetNoIndex();
    usePageView(strings.title);

    const queryParams = queryStringHelpers.objectify(window.location.search);
    const windowUrl = window.location.href;

    const [forgotInfo, setForgotInfo] = useState(EMPTY_FORGOT_INFO);
    const [isNameExpanded, setIsNameExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validation, setValidation] = useState<validationLib.Validation>({});
    const [validationCount, setValidationCount] = useState(1);
    const formValidation = validation.errors ?? EMPTY_FORGOT_INFO_VALIDATION;

    const continueBtnRef = useRef(null);
    const [expiredUrlCode, setExpiredUrlCode] = useState("");
    const [expiredModal, setExpiredModal] = useState(false);

    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    function closeExpiredModal() {
        setExpiredModal(false);
    }
    function closeExpiredModalAndRedirect() {
        if (!isMountedRef.current) {
            return;
        }
        setExpiredModal(false);
        const paramsToStringify: {[key: string]: string} = {
            ...queryParams,
            source: queryStringHelpers.Values.PalExpired,
        };
        if (expiredUrlCode) {
            paramsToStringify.cid = expiredUrlCode;
        }
        const stringParams = queryStringHelpers.stringify(paramsToStringify);
        props.history.push(`${routes.ORGANIC_APP}${stringParams}`);
    }
    function openExpiredModal() {
        setExpiredModal(true);
    }

    function onBlurInput(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;
        if (isApplicationKey(name) || isAddressKey(name)) {
            const {isValid, messages} = validateApplicationField(name, value);
            if (isValid) {
                return;
            }

            setValidation((prev) => ({
                ...prev,
                errors: {
                    ...prev?.errors,
                    [name]: messages,
                },
            }));
        }
    }

    function onSubmit(e: React.FormEvent) {
        e.preventDefault();

        if (hasFailingValidation(validation)) {
            return;
        }

        setLoading(true);
        endpoints
            .forgotInvite(forgotInfo, getUserIds().visitID)
            .then((data) => {
                if (!isMountedRef.current) {
                    return;
                }
                if (data.status === InviteValidationStatus.active) {
                    props.history.push(`${routes.INVITE_APP}${window.location.search}`, {
                        ...data.applicationInfo,
                    });
                } else if (data.status === InviteValidationStatus.expired) {
                    // Show Modal and redirect to Oragnic w/ urlCode
                    if (data.urlCode) {
                        setExpiredUrlCode(data.urlCode);
                    }
                    openExpiredModal();
                } else if (data.status === InviteValidationStatus.tooManyAttempts) {
                    // Redirect to Organic w/ urlCode
                    const paramsToStringify: {[key: string]: string} = {
                        ...queryParams,
                        source: queryStringHelpers.Values.PalForgot,
                    };
                    if (data.urlCode) {
                        paramsToStringify.cid = data.urlCode;
                    }
                    const stringParams = queryStringHelpers.stringify(paramsToStringify);
                    props.history.push(`${routes.ORGANIC_APP}${stringParams}`);
                }
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                const errValidation = validationLib.getValidationObj(err);
                setValidation(errValidation);
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setLoading(false);
                setValidationCount((prev) => prev + 1);
            });
    }

    function clearValidation(validationKey: AppKeys | AddressKeys) {
        setValidation((prev) => {
            if (prev.generic || (prev.errors && prev.errors[validationKey])) {
                const nextValidation = {
                    ...prev,
                };
                delete nextValidation.generic;
                if (prev && prev.errors) {
                    delete prev.errors[validationKey];
                }
                return nextValidation;
            }
            return prev;
        });
    }

    function onChangeForgotInfoField(name: AppKeys | AddressKeys, value: string | boolean) {
        setForgotInfo((prevForgotInfo) => ({
            ...prevForgotInfo,
            [name]: value,
        }));
        clearValidation(name);
    }

    function onChangeName(field: AppKeys, value: string) {
        onChangeForgotInfoField(field, value);
    }

    const dobRef = useRef<HTMLInputElement>(null);
    const addressRef = useRef<HTMLInputElement>(null);

    const firstInvalidKey = getFirstInvalidKey(validation, ValidationType.Application);
    let firstInvalidRef: React.RefObject<HTMLInputElement> | undefined = undefined;
    if (firstInvalidKey === AppKeys.DateOfBirth) {
        firstInvalidRef = dobRef;
    } else if (firstInvalidKey === AddressKeys.Zipcode) {
        firstInvalidRef = addressRef;
    }
    useFocusInvalid(validationCount, firstInvalidRef?.current);

    return (
        <ErrorBoundary>
            <LandingPageWrapper
                renderInterestRates={props.renderInterestRates}
                renderBlueGradient={false}
            >
                <form onSubmit={onSubmit}>
                    <Container>
                        <Row className={styles.row}>
                            <Column large={8} className={styles.cardsImageColumn}>
                                <div className={styles.cardsWrapper}>
                                    <img
                                        className={styles.cardsImage}
                                        src={cardsImage}
                                        srcSet={`${cardsImage} 1x, ${cardsImage2} 2x, ${cardsImage3} 3x`}
                                        alt={strings.card}
                                    />
                                </div>
                            </Column>
                            <Column large={4} className={styles.infoColumn}>
                                <div className={styles.description}>
                                    {props.findInviteDescription
                                        ? props.findInviteDescription
                                        : strings.description}
                                </div>
                                <ExpandableName
                                    firstName={forgotInfo[AppKeys.FirstName]}
                                    isExpanded={isNameExpanded}
                                    lastName={forgotInfo[AppKeys.LastName]}
                                    middleInitial={forgotInfo[AppKeys.MiddleInitial]}
                                    onBlurInput={onBlurInput}
                                    onChangeName={onChangeName}
                                    setIsExpanded={(isExpanded: boolean) =>
                                        setIsNameExpanded(isExpanded)
                                    }
                                    firstInvalidKey={firstInvalidKey}
                                    validationFirst={formValidation[AppKeys.FirstName]}
                                    validationLast={formValidation[AppKeys.LastName]}
                                    validationMiddle={formValidation[AppKeys.MiddleInitial]}
                                    validationCount={validationCount}
                                />
                                <DateOfBirthInput
                                    id={AppKeys.DateOfBirth}
                                    name={AppKeys.DateOfBirth}
                                    label={strings.dateOfBirth}
                                    ref={dobRef}
                                    onBlur={onBlurInput}
                                    value={forgotInfo[AppKeys.DateOfBirth]}
                                    setValue={(value) => {
                                        onChangeForgotInfoField(AppKeys.DateOfBirth, value);
                                    }}
                                    required={true}
                                    validationMessage={formValidation[AppKeys.DateOfBirth]}
                                    inputMode="numeric"
                                />
                                <LinedInput
                                    id={AddressKeys.Zipcode}
                                    name={AddressKeys.Zipcode}
                                    label={strings.zipcode}
                                    onBlur={onBlurInput}
                                    ref={addressRef}
                                    setValue={(value) => {
                                        onChangeForgotInfoField(AddressKeys.Zipcode, value);
                                    }}
                                    required={true}
                                    type={InputType.Text}
                                    value={forgotInfo[AddressKeys.Zipcode]}
                                    maxLength={5}
                                    minLength={5}
                                    pattern="\d{5}"
                                    inputMode="numeric"
                                    validationMessage={formValidation[AddressKeys.Zipcode]}
                                />

                                {validation.generic ? (
                                    <ValidationList
                                        className={styles.validation}
                                        messages={validation.generic}
                                    />
                                ) : null}

                                <ActionButton
                                    className={styles.continueBtn}
                                    fullWidth={true}
                                    label={strings.continue}
                                    loading={loading}
                                    onClick={() =>
                                        callApplicationLead("forgot-submit", null, windowUrl)
                                    }
                                />
                            </Column>
                            <ExpiredInviteModal
                                label={strings.expiredModal}
                                isOpen={expiredModal}
                                onClose={closeExpiredModal}
                                onSubmit={closeExpiredModalAndRedirect}
                                onSubmitLabel={strings.continue}
                                buttonRef={continueBtnRef}
                            />
                        </Row>
                    </Container>
                </form>
            </LandingPageWrapper>
        </ErrorBoundary>
    );
}

ForgotInvite.defaultProps = {
    renderInterestRates: false,
};
